import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { contentService, VERSE_REVIEWED_EVENT } from '../services/content';
import { Verse, VerseStatus } from '../types/content';

export interface SubmissionGroup {
  beatId: string;
  beatTitle: string;
  genre: string;
  bpm: number;
  verses: Verse[];
}

/** Verses submitted on the signed-in producer's beats, grouped by beat. */
export function useVerseSubmissions(producerId?: string) {
  const [statusFilter, setStatusFilter] = useState<VerseStatus>('pending');
  const [submissions, setSubmissions] = useState<Verse[]>([]);
  const [loading, setLoading] = useState(!!producerId);
  const [error, setError] = useState<string | null>(null);
  const [busyVerseId, setBusyVerseId] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!producerId) {
      setSubmissions([]);
      setLoading(false);
      return;
    }
    try {
      const rows = await contentService.getSubmissions(producerId, statusFilter);
      if (!mountedRef.current) return;
      setSubmissions(rows);
      setError(null);
    } catch (err) {
      if (!mountedRef.current) return;
      setError(err instanceof Error ? err.message : 'Could not load your submissions');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [producerId, statusFilter]);

  useEffect(() => {
    setLoading(!!producerId);
    refresh();
  }, [producerId, refresh]);

  const review = useCallback(
    async (verseId: string, status: 'approved' | 'declined') => {
      setBusyVerseId(verseId);
      try {
        await contentService.reviewVerse(verseId, status);
        if (!mountedRef.current) return;
        // The row leaves the queue either way.
        setSubmissions(prev => prev.filter(v => v.id !== verseId));
        setError(null);
      } catch (err) {
        if (!mountedRef.current) return;
        setError(err instanceof Error ? err.message : 'Could not save your decision');
      } finally {
        if (mountedRef.current) setBusyVerseId(null);
      }
    },
    []
  );

  const groups = useMemo((): SubmissionGroup[] => {
    const byBeat = new Map<string, SubmissionGroup>();
    submissions.forEach(verse => {
      const beat = verse.beat;
      if (!beat) return;
      const existing = byBeat.get(beat.id);
      if (existing) {
        existing.verses.push(verse);
        return;
      }
      byBeat.set(beat.id, {
        beatId: beat.id,
        beatTitle: beat.title,
        genre: beat.genre,
        bpm: beat.bpm,
        verses: [verse],
      });
    });
    return Array.from(byBeat.values());
  }, [submissions]);

  return {
    submissions,
    groups,
    loading,
    error,
    refresh,
    review,
    busyVerseId,
    statusFilter,
    setStatusFilter,
  };
}

/** Pending submission count for the producer's nav badge. */
export function usePendingVerseCount(enabled: boolean) {
  const [pendingCount, setPendingCount] = useState(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled) {
      setPendingCount(0);
      return;
    }
    try {
      const count = await contentService.getPendingSubmissionsCount();
      if (mountedRef.current) setPendingCount(count);
    } catch {
      // Badge is non-critical.
    }
  }, [enabled]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Submissions arrive rarely, so a focus check beats holding a subscription.
  useEffect(() => {
    if (!enabled) return;
    const handler = () => refresh();
    window.addEventListener('focus', handler);
    window.addEventListener(VERSE_REVIEWED_EVENT, handler);
    return () => {
      window.removeEventListener('focus', handler);
      window.removeEventListener(VERSE_REVIEWED_EVENT, handler);
    };
  }, [enabled, refresh]);

  return { pendingCount, refresh };
}
